// Directives
import directives from '../directives/index.js'

// Libraries
import Vue from 'vue'

// Services
import router from '../router'
import store from '../store'

// Utilities
import analytics from '../utility/analyticsUtil'
import isEmpty from '../utility/isEmpty'
import logger from '../utility/loggerUtil'
import sessionRecord from '../utility/sessionRecordUtil'
import session from '../utility/sessionUtil.js'
import { getDefaultState } from '../utility/applicationUtil'
import { KeyConstants } from '../constants'

export default class StartupService {
  static async init () {
    this.initPolyfills()
    await this.initServiceWorker()
    this.initDeviceInfo()
    this.initDirectives()
    this.initGlobalRouteGuards()
    this.initSiteData()

    await this.getConfigSettings()
    await StartupService.initUserDataCollection()
  }

  static async initUserDataCollection () {
    logger.debug('Initializing user data collection services...')

    await StartupService.initAnalytics()
  }

  static initPolyfills () {
    if (!Array.prototype.includes) {
      Object.defineProperty(Array.prototype, 'includes', { // eslint-disable-line
        value: function (searchElement, fromIndex) {
          if (this == null) {
            throw new TypeError('"this" is null or not defined')
          }

          // 1. Let O be ? ToObject(this value).
          const o = Object(this)

          // 2. Let len be ? ToLength(? Get(O, "length")).
          const len = o.length >>> 0

          // 3. If len is 0, return false.
          if (len === 0) {
            return false
          }

          // 4. Let n be ? ToInteger(fromIndex).
          //    (If fromIndex is undefined, this step produces the value 0.)
          const n = fromIndex | 0

          // 5. If n ≥ 0, then
          //  a. Let k be n.
          // 6. Else n < 0,
          //  a. Let k be len + n.
          //  b. If k < 0, let k be 0.
          let k = Math.max(n >= 0 ? n : len - Math.abs(n), 0)

          function sameValueZero (x, y) {
            return x === y || (typeof x === 'number' && typeof y === 'number' && isNaN(x) && isNaN(y))
          }

          // 7. Repeat, while k < len
          while (k < len) {
            // a. Let elementK be the result of ? Get(O, ! ToString(k)).
            // b. If SameValueZero(searchElement, elementK) is true, return true.
            if (sameValueZero(o[k], searchElement)) {
              return true
            }
            // c. Increase k by 1.
            k++
          }

          // 8. Return false
          return false
        }
      })
    }
  }

  static async initAnalytics () {
    await analytics.init()

    analytics.saveInitUserProperties()
    sessionRecord.saveInitUserProperties()
  }

  static initDirectives () {
    // Formats a currency input to 2 decimal precision
    // TODO: Fix jerky input when typing in middle or before $
    Vue.directive('currency', directives.CurrencyDirective)

    // Auto focuses the element on page load
    Vue.directive('focus', directives.FocusDirective)

    // Formats input to currency and prevents non-numeric inputs
    Vue.directive('inputCurrency', directives.InputCurrencyDirective)

    Vue.directive('masked', directives.MaskedDirective)

    // Prevents non-numeric inputs
    Vue.directive('numeric', directives.NumericDirective)

    Vue.directive('uppercase', directives.UppercaseDirective)
  }

  static initDeviceInfo () {
    store.commit('display/setDeviceBrowserInfo')
  }

  static async getConfigSettings () {
    try {
      const defaultState = getDefaultState()
      const loanProductType = store.getters['applicationCustomer/loanProduct']
      await Promise.all([
        await store.dispatch('session/getConfigSettings'),
        await store.dispatch('applicationSession/getProductConfiguration', { state: defaultState, productType: loanProductType })
      ])
    } catch (e) {
      logger.error('Error attempting to get config settings')
      logger.debug(e)
    }
  }

  /**
   * Initializes route guards to prevent or redirect navigation depending on defined conditions.
   */
  static initGlobalRouteGuards () {
    const navigationAllowed = (to, from) => { // Checks if navigation is allowed from one specific route to another
      if (!isEmpty(from.meta) && !isEmpty(from.meta.preventNavigation) && from.meta.preventNavigation.includes(to.name)) {
        return false
      }

      return true
    }

    router.beforeEach((to, from, next) => {
      if (navigationAllowed(to, from) === false) { // Create more functions and add them here to alter navigation with different conditions
        next(false)
        return
      }

      next()
    })
  }

  /**
   * Registers a new service worker, required for PWA config.
   */
  static async initServiceWorker () {
    if (!('serviceWorker' in navigator)) {
      logger.debug('ServiceWorker not supported')
      return
    }

    try {
      const registration = await navigator.serviceWorker.register('/serviceWorker.js')

      logger.debug('ServiceWorker registration successful with scope: ' + registration.scope)
    } catch (e) {
      logger.error('Error registering service worker')
      logger.debug(e)
    }
  }

  static initSiteData () {
    try {
      const qrScannedStoreNumber = session.getCookie(KeyConstants.cookies.qrScanStoreNumberRedirect)

      if (!isEmpty(qrScannedStoreNumber)) {
        store.commit('applicationRetailStore/setQrScannedStoreNumber', { qrScannedStoreNumber })
      }
    } catch (error) {
      logger.error('Error checking for QR scanned store number')
      logger.debug(error)
    }
  }
}
